// app/src/modules/ui/BottomNav.tsx (Bootstrap)
// Mobile bottom navigation shown on every page except the till and login. A few
// primary destinations sit on the bar; everything else lives in a "More" sheet.
// The structural classes (.bottom-nav, .bottom-nav-sheet*) keep the responsive
// show/hide + sheet animation; ad-hoc styling uses Bootstrap utilities + inline tokens.
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSession } from "../../lib/session";
import { useNav } from "../../lib/nav";

const I = (paths: React.ReactNode) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{paths}</svg>
);
const ICON = {
  till: I(<><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><path d="M3 6h18" /><path d="M16 10a4 4 0 0 1-8 0" /></>),
  workshop: I(<path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />),
  back: I(<><rect x="3" y="3" width="7" height="9" rx="1" /><rect x="14" y="3" width="7" height="5" rx="1" /><rect x="14" y="12" width="7" height="9" rx="1" /><rect x="3" y="16" width="7" height="5" rx="1" /></>),
  more: I(<><circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" /></>),
};

const PRIMARY = [
  { to: "/", label: "Store", icon: ICON.till, match: (p: string) => p === "/" },
  { to: "/backoffice", label: "Back office", icon: ICON.back, match: (p: string) => p === "/backoffice" },
];

export default function BottomNav() {
  const sections = useNav();
  const GROUPS: { title: string; items: { to: string; label: string }[] }[] = [
    { title: "Store", items: [{ to: "/", label: "Store (till)" }, { to: "/backoffice", label: "Dashboard" }] },
    ...sections.map((c) => ({ title: c.label, items: c.items.map((it) => ({ to: it.to, label: it.label })) })),
    { title: "Help", items: [{ to: "/staff-guide.pdf", label: "Staff guide" }] },
  ];
  const loc = useLocation();
  const nav = useNavigate();
  const { user, logout } = useSession();
  const [more, setMore] = useState(false);

  if (!user || loc.pathname === "/login" || loc.pathname === "/") return null;
  const path = loc.pathname;
  const onPrimary = PRIMARY.some((p) => p.match(path));

  const go = (to: string) => { setMore(false); void nav(to); };

  return (
    <>
      <nav className="bottom-nav" aria-label="Sections">
        {PRIMARY.map((p) => {
          const on = p.match(path);
          return (
            <button key={p.to} className="bottom-nav-btn" style={on ? { color: "var(--accent-text)" } : undefined} aria-current={on ? "page" : undefined} onClick={() => nav(p.to)}>
              {p.icon}<span>{p.label}</span>
            </button>
          );
        })}
        <button className="bottom-nav-btn" style={!onPrimary ? { color: "var(--accent-text)" } : undefined} aria-expanded={more} onClick={() => setMore(true)}>
          {ICON.more}<span>More</span>
        </button>
      </nav>

      {more && (
        <div className="bottom-nav-sheet-scrim" onClick={() => setMore(false)}>
          <div className="bottom-nav-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="All sections">
            <div className="bottom-nav-sheet-head">
              <span style={{ fontSize: 16, fontWeight: 800 }}>All sections</span>
              <button aria-label="Close" onClick={() => setMore(false)} style={{ height: 36, width: 36, borderRadius: 4, border: "1px solid var(--line)", background: "var(--paper-2)", color: "var(--ink)" }}>✕</button>
            </div>
            <div style={{ overflowY: "auto", paddingLeft: 16, paddingRight: 16, paddingTop: 4, paddingBottom: "max(16px,env(safe-area-inset-bottom))" }}>
              {GROUPS.map((g) => (
                <div key={g.title} style={{ marginTop: 14 }}>
                  <div className="fw-bold text-text-uppercase" style={{ marginBottom: 6, fontSize: 11, letterSpacing: ".05em", color: "var(--muted)" }}>{g.title}</div>
                  <div className="d-grid" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))", gap: 8 }}>
                    {g.items.map((it) => {
                      const on = path === it.to || (it.to !== "/" && path.startsWith(it.to + "/"));
                      return (
                        <button key={it.label} onClick={() => it.to.endsWith(".pdf") ? (setMore(false), window.open(it.to, "_blank", "noopener")) : go(it.to)}
                          className="text-start fw-semibold"
                          style={{ borderRadius: 4, border: "1px solid var(--line)", padding: "12px 14px", fontSize: 14, ...(on ? { background: "var(--accent)", color: "#fff" } : { background: "var(--paper-2)", color: "var(--ink)" }) }}>
                          {it.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              <button onClick={async () => { setMore(false); await logout(); void nav("/login"); }}
                className="w-100 fw-bold"
                style={{ marginTop: 18, borderRadius: 4, border: "1px solid var(--accent)", background: "transparent", padding: "13px 0", fontSize: 15, color: "var(--accent-text)" }}>
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
